import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Music, Play, Pause, X } from 'lucide-react';
import { format } from 'date-fns';
import DatePicker from '@/components/DatePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GymnastsTab() {
  const [gymnasts, setGymnasts] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [gymnastToDelete, setGymnastToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [musicFile, setMusicFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [useLegalName, setUseLegalName] = useState(false);
  const [showMusicPlayer, setShowMusicPlayer] = useState(false);
  const [playingGymnast, setPlayingGymnast] = useState(null);
  const [musicDuration, setMusicDuration] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const [formData, setFormData] = useState({
    legalFirstName: '',
    legalLastName: '',
    preferredFirstName: '',
    preferredLastName: '',
    dob: '',
    bgNumber: ''
  });

  // Format duration as Xm Ys
  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  // Get floor music URL via proxy (handles R2 auth)
  const getFloorMusicUrl = (gymnastId) => {
    return `${BACKEND_URL}/api/audio/floor-music/${gymnastId}`;
  };

  // Handle listen to music
  const handleListenToMusic = (gymnast) => {
    setPlayingGymnast(gymnast);
    setMusicDuration(null);
    setIsPlaying(false);
    setShowMusicPlayer(true);
  };

  // Handle audio loaded
  const handleAudioLoaded = () => {
    if (audioRef.current) {
      setMusicDuration(audioRef.current.duration);
      // Try to autoplay - browsers may block this
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((err) => {
          console.log('Autoplay blocked, user must click play:', err);
          setIsPlaying(false);
        });
    }
  };

  // Handle play/pause
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle close music player
  const closeMusicPlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setShowMusicPlayer(false);
    setPlayingGymnast(null);
    setIsPlaying(false);
  };

  useEffect(() => {
    loadGymnasts();
  }, []);

  const loadGymnasts = async () => {
    try {
      const response = await axios.get(`${API}/gymnasts`);
      setGymnasts(response.data);
    } catch (error) {
      console.error('Failed to load gymnasts:', error);
      toast.error('Failed to load gymnasts');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // If legal name changes and useLegalName is checked, update preferred name too
    if (useLegalName && (name === 'legalFirstName' || name === 'legalLastName')) {
      if (name === 'legalFirstName') {
        setFormData(prev => ({ ...prev, legalFirstName: value, preferredFirstName: value }));
      } else if (name === 'legalLastName') {
        setFormData(prev => ({ ...prev, legalLastName: value, preferredLastName: value }));
      }
    }
  };

  const handleUseLegalNameToggle = (checked) => {
    setUseLegalName(checked);
    if (checked) {
      // Copy legal name to preferred name
      setFormData(prev => ({
        ...prev,
        preferredFirstName: prev.legalFirstName,
        preferredLastName: prev.legalLastName
      }));
    }
  };

  const handleOpenDialog = (gymnast = null) => {
    if (gymnast) {
      setEditing(gymnast);
      const legalFirst = gymnast.legalFirstName || gymnast.firstName || '';
      const legalLast = gymnast.legalLastName || gymnast.lastName || '';
      const preferredFirst = gymnast.preferredFirstName || gymnast.firstName || '';
      const preferredLast = gymnast.preferredLastName || gymnast.lastName || '';
      
      setFormData({
        legalFirstName: legalFirst,
        legalLastName: legalLast,
        preferredFirstName: preferredFirst,
        preferredLastName: preferredLast,
        dob: gymnast.dob,
        bgNumber: gymnast.bgNumber
      });
      
      // Check if preferred name matches legal name
      setUseLegalName(legalFirst === preferredFirst && legalLast === preferredLast);
    } else {
      setEditing(null);
      setFormData({
        legalFirstName: '',
        legalLastName: '',
        preferredFirstName: '',
        preferredLastName: '',
        dob: '',
        bgNumber: ''
      });
      setUseLegalName(false);
    }
    setMusicFile(null);
    setShowDialog(true);
  };

  const handleMusicFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.includes('audio/mpeg') && !file.type.includes('audio/mp3')) {
        toast.error('Only MP3 files are allowed');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setMusicFile(file);
    }
  };

  const handleUploadMusic = async (gymnastId) => {
    if (!musicFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', musicFile);

      await axios.post(
        `${API}/gymnasts/${gymnastId}/upload-music`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Floor music uploaded successfully');
      setMusicFile(null);
      loadGymnasts();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload floor music');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Manual validation
    if (!formData.legalFirstName || !formData.legalLastName || !formData.preferredFirstName || 
        !formData.preferredLastName || !formData.dob || !formData.bgNumber) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!editing && !musicFile) {
      toast.error('Please upload floor music (MP3)');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/gymnasts/${editing.id}`, formData);
        
        // Upload music if file selected
        if (musicFile) {
          await handleUploadMusic(editing.id);
        }
        
        toast.success('Gymnast updated successfully');
      } else {
        const response = await axios.post(`${API}/gymnasts`, formData);
        
        // Upload music if file selected
        if (musicFile && response.data?.id) {
          await handleUploadMusic(response.data.id);
        }
        
        toast.success('Gymnast added successfully');
      }
      
      // Close dialog and reload after everything is complete
      setShowDialog(false);
      loadGymnasts();
    } catch (error) {
      console.error('Failed to save gymnast:', error);
      toast.error(error.response?.data?.detail || 'Failed to save gymnast');
    }
  };

  const handleDelete = async (gymnast) => {
    setGymnastToDelete(gymnast);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!gymnastToDelete) return;

    try {
      await axios.delete(`${API}/gymnasts/${gymnastToDelete.id}`);
      toast.success(`${gymnastToDelete.firstName} ${gymnastToDelete.lastName}'s profile deleted successfully`);
      loadGymnasts();
    } catch (error) {
      console.error('Failed to delete gymnast:', error);
      toast.error('Failed to delete gymnast');
    } finally {
      setShowDeleteDialog(false);
      setGymnastToDelete(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Gymnasts</h2>
          <p className="text-gray-600 text-sm mt-1">Manage your club's gymnasts</p>
        </div>
        <Button
          data-testid="add-gymnast-button"
          onClick={() => handleOpenDialog()}
          className="bg-[#000000] hover:bg-[#333333]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Gymnast
        </Button>
      </div>

      {gymnasts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No gymnasts added yet. Click "Add Gymnast" to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {gymnasts.map(gymnast => (
            <Card 
              key={gymnast.id} 
              data-testid={`gymnast-card-${gymnast.id}`} 
              className={`hover:shadow-lg transition-shadow h-full flex flex-col ${!gymnast.floorMusicUrl ? 'border-4 border-gray-900 shadow-gray-300 shadow-lg' : ''}`}
            >
              <CardHeader>
                <CardTitle className="text-lg">
                  {gymnast.preferredFirstName || gymnast.firstName} {gymnast.preferredLastName || gymnast.lastName}
                </CardTitle>
                {(() => {
                  const preferredFirst = gymnast.preferredFirstName || gymnast.firstName || '';
                  const preferredLast = gymnast.preferredLastName || gymnast.lastName || '';
                  const legalFirst = gymnast.legalFirstName || gymnast.firstName || '';
                  const legalLast = gymnast.legalLastName || gymnast.lastName || '';
                  
                  // Only show legal name if it's different from preferred name
                  const isDifferent = preferredFirst !== legalFirst || preferredLast !== legalLast;
                  
                  if (isDifferent) {
                    return (
                      <CardDescription className="text-xs">
                        Legal Name: {legalFirst} {legalLast}
                      </CardDescription>
                    );
                  }
                  return null;
                })()}
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between">
                <div className="space-y-2 text-sm mb-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">DOB:</span>
                    <span className="font-medium">{format(new Date(gymnast.dob), 'dd/MM/yyyy')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">BG Number:</span>
                    <span className="font-medium">{gymnast.bgNumber}</span>
                  </div>
                  {gymnast.floorMusicUrl ? (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Floor Music:</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleListenToMusic(gymnast);
                          }}
                          className="text-gray-500 hover:text-black transition-colors p-1"
                          title="Listen to music"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <a
                          href={getFloorMusicUrl(gymnast.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#000000] hover:underline text-xs"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Download
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-100 border-2 border-gray-900 rounded p-2 mt-2 animate-pulse">
                      <div className="flex items-center gap-2">
                        <span className="text-xl animate-bounce">🚨</span>
                        <div className="flex-1">
                          <p className="text-gray-900 font-bold text-xs uppercase leading-tight">
                            NO MUSIC
                          </p>
                          <p className="text-gray-800 font-semibold text-[10px] leading-tight">
                            {gymnast.floorMusicRemovedByAdmin 
                              ? "Removed by admin"
                              : "Upload required"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-auto">
                  <Button
                    data-testid={`edit-gymnast-button-${gymnast.id}`}
                    onClick={() => handleOpenDialog(gymnast)}
                    size="sm"
                    variant={!gymnast.floorMusicUrl ? "default" : "outline"}
                    className={`flex-1 text-xs ${!gymnast.floorMusicUrl ? 'bg-gray-900 hover:bg-black text-white border-gray-900 font-bold animate-pulse' : ''}`}
                  >
                    {!gymnast.floorMusicUrl ? (
                      <>
                        <Music className="w-4 h-4 mr-1" />
                        SUBMIT MUSIC
                      </>
                    ) : (
                      <>
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </>
                    )}
                  </Button>
                  <Button
                    data-testid={`delete-gymnast-button-${gymnast.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(gymnast);
                    }}
                    size="sm"
                    variant="outline"
                    className="text-gray-900 hover:bg-gray-100"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={(open) => {
        setShowDialog(open);
        if (!open) {
          // Reset form when dialog closes
          setEditing(null);
          setMusicFile(null);
          setFormData({
            legalFirstName: '',
            legalLastName: '',
            preferredFirstName: '',
            preferredLastName: '',
            dob: '',
            bgNumber: ''
          });
          setUseLegalName(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Gymnast' : 'Add Gymnast'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update gymnast details' : 'Add a new gymnast to your club'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <h3 className="font-semibold text-sm mb-2">Legal Name (as on British Gymnastics)</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="legalFirstName">Legal First Name *</Label>
                  <Input
                    id="legalFirstName"
                    name="legalFirstName"
                    data-testid="gymnast-legal-first-name-input"
                    required
                    value={formData.legalFirstName}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Label htmlFor="legalLastName">Legal Last Name *</Label>
                  <Input
                    id="legalLastName"
                    name="legalLastName"
                    data-testid="gymnast-legal-last-name-input"
                    required
                    value={formData.legalLastName}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm">Preferred Name (for Programme & Results)</h3>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="useLegalName"
                    checked={useLegalName}
                    onCheckedChange={handleUseLegalNameToggle}
                  />
                  <Label htmlFor="useLegalName" className="text-xs font-normal cursor-pointer">
                    Use legal name
                  </Label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="preferredFirstName">Preferred First Name *</Label>
                  <Input
                    id="preferredFirstName"
                    name="preferredFirstName"
                    data-testid="gymnast-preferred-first-name-input"
                    required
                    value={formData.preferredFirstName}
                    onChange={handleChange}
                    disabled={useLegalName}
                    className={useLegalName ? 'bg-gray-100' : ''}
                  />
                </div>
                <div>
                  <Label htmlFor="preferredLastName">Preferred Last Name *</Label>
                  <Input
                    id="preferredLastName"
                    name="preferredLastName"
                    data-testid="gymnast-preferred-last-name-input"
                    required
                    value={formData.preferredLastName}
                    onChange={handleChange}
                    disabled={useLegalName}
                    className={useLegalName ? 'bg-gray-100' : ''}
                  />
                </div>
              </div>
            </div>

            <div>
              <DatePicker
                key={editing ? editing.id : 'new'}
                label="Date of Birth"
                value={formData.dob}
                onChange={(date) => setFormData(prev => ({ ...prev, dob: date }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="bgNumber">BG Number *</Label>
              <Input
                id="bgNumber"
                name="bgNumber"
                data-testid="gymnast-bg-number-input"
                required
                value={formData.bgNumber}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="floorMusic">Floor Music (MP3) *</Label>
              {editing?.floorMusicUrl && (
                <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded">
                  <p className="text-xs text-green-700 mb-1">✓ Current music uploaded</p>
                  <a
                    href={getFloorMusicUrl(editing.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#000000] hover:underline text-xs"
                  >
                    Download current music
                  </a>
                </div>
              )}
              <input
                id="floorMusic"
                name="floorMusic"
                data-testid="gymnast-floor-music-input"
                type="file"
                accept="audio/mpeg,audio/mp3"
                onChange={handleMusicFileChange}
                required={!editing?.floorMusicUrl}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-[#000000] file:text-white
                  hover:file:bg-[#333333]
                  file:cursor-pointer"
              />
              {musicFile && (
                <p className="text-xs text-green-600 mt-1">New music selected: {musicFile.name}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">File will be renamed to: [First Name] [Last Name] - [Club Name] - [Category].mp3</p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-gymnast-button"
                className="flex-1 bg-[#000000] hover:bg-[#333333]"
              >
                {editing ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Gymnast</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {gymnastToDelete?.firstName} {gymnastToDelete?.lastName}'s profile? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-gray-900 hover:bg-black"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Music Player Popup */}
      {showMusicPlayer && playingGymnast && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeMusicPlayer}>
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-xs w-full mx-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeMusicPlayer}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Scorz Logo */}
            <div className="flex justify-center mb-4">
              <img src="/scorz-logo.png" alt="Scorz" className="h-12 w-auto" />
            </div>
            
            {/* Gymnast Name */}
            <p className="text-center font-semibold text-lg mb-2">
              {playingGymnast.preferredFirstName || playingGymnast.firstName} {playingGymnast.preferredLastName || playingGymnast.lastName}
            </p>
            
            {/* Duration */}
            <p className="text-center text-gray-500 text-sm mb-4">
              {musicDuration ? formatDuration(musicDuration) : 'Loading...'}
            </p>
            
            {/* Play/Pause Button */}
            <div className="flex justify-center">
              <button
                onClick={togglePlayPause}
                disabled={!musicDuration}
                className={`w-12 h-12 rounded-full bg-black text-white flex items-center justify-center transition-colors ${musicDuration ? 'hover:bg-gray-800' : 'opacity-50 cursor-not-allowed'}`}
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
            </div>
            
            {/* Audio Element */}
            <audio
              ref={audioRef}
              src={getFloorMusicUrl(playingGymnast.id)}
              onLoadedMetadata={handleAudioLoaded}
              onCanPlayThrough={() => {
                if (audioRef.current && !musicDuration) {
                  setMusicDuration(audioRef.current.duration);
                }
              }}
              onEnded={() => setIsPlaying(false)}
              onError={(e) => {
                console.error('Audio error:', e.target.error);
                toast.error('Failed to load music');
                setMusicDuration(0);
              }}
              preload="auto"
            />
          </div>
        </div>
      )}
    </div>
  );
}